import {
  CalculatorParticipantType,
  CalculatorSplitMethod,
  IBeneficiary,
  ICalculatorParticipant,
  ICalculatorRule,
  ICalculatorRuleRemainder,
  ICalculatorRules,
  ICalculatorRuleSharePolicy,
  ICalculatorRuleSharePolicyShare,
  ICategoryGroup,
  ICreateUpdateCalculatorRules,
  IRemainderBeneficiary,
  IRemainderCondition,
  RuleFundSource,
  ShareEligibilityMethod,
  ShareRuleType,
  TipCalculatorMethod,
} from './CalculatorRule';
import { Days, IDaySegment } from './DaySegment';
import { IPOSEmployee, IPOSProvider, IPOSResults, IPOSSegment, IPOSTicket, IPOSTicketCategory, IPOSTicketIgnoreOverride, IPOSTicketSplitOverride, ISplitParticipant, TicketOverrideType } from './Integration';
import { IJob } from './Job';

export const dayOptions = [
  { label: 'Sun', value: Days.SUNDAY },
  { label: 'Mon', value: Days.MONDAY },
  { label: 'Tue', value: Days.TUESDAY },
  { label: 'Wed', value: Days.WEDNESDAY },
  { label: 'Thu', value: Days.THURSDAY },
  { label: 'Fri', value: Days.FRIDAY },
  { label: 'Sat', value: Days.SATURDAY },
];

export enum TipCalculationsTab {
  TIP_SHARES = 'tip-shares',
  DEFAULT_POOL = 'default-pool',
  POINT_VALUES = 'point-values',
}

export enum SourceType {
  AUTO_GRATUITIES = 'autoGratuity',
  TIP = 'tip',
}

export enum SourcePaymentType {
  CASH = 'cash',
  CREDIT = 'creditCard',
  CREDIT_FEE = 'creditCardFee',
}

export interface ICalculated {
  points: number;
  totalPerHour: number;
  total: number;
}

export enum TipCalculatorReceivedDistributionType {
  REMAINDER = 'remainder',
  SHARE = 'share',
}

export enum TipCalculatorWarningType {
  CAPPED_SHARE = 'capped_share',
  CAPPED_REMAINDER = 'capped_remainder',
  UNALLOCATED_REMAINDER = 'unallocated_remainder',
}

export interface ITipCalculatorBaseTotals {
  gross: number;
  net: number;
  tip: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
  autoGratuity: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
}

export interface ITipCalculatorTicketTotals extends ITipCalculatorBaseTotals {}

export interface ITipCalculatorEmployeeTotals extends ITipCalculatorBaseTotals {
  totalMinWorked: number;
}

export interface ITipCalculatorTicketCategory {
  name: string;
  price: number;
  quantity: number;
  discounts: number;
  tax: number;
  total: number;
}

export interface ITipCalculatorTicketSplitOverrideShare {
  participant: ISplitParticipant;
  percent: number;
  gross: number;
  tip: number;
  autoGratuity: number;
  sources: RuleFundSource[];
}

export interface ITipCalculatorTicketSplitOverride {
  type: TicketOverrideType.SPLIT;
  shares: ITipCalculatorTicketSplitOverrideShare[];
}

export interface ITipCalculatorTicketIgnoreOverride {
  type: TicketOverrideType.IGNORE;
}

/* BEGIN -- Tip Calculator Request -- BEGIN */
export interface ITipCalculatorRequest {
  segmentId: string;
  segment: string;
  start: string;
  end: string;
  employees: ITipCalculatorRequestEmployee[];
  participants?: ICalculatorParticipant[];
  rules: ICalculatorRule[];
  categoryGroups: ICategoryGroup[];
}

export interface ITipCalculatorRequestEmployee {
  id: string;
  name: string;
  job: IJob;
  schedules: ITipCalculatorRequestEmployeeSchedule[];
  tickets: ITipCalculatorRequestTicket[];
  totals: ITipCalculatorEmployeeTotals;
}

export interface ITipCalculatorRequestEmployeeSchedule {
  start: string;
  end: string;
}

export interface ITipCalculatorRequestTicket {
  ticketId: string;
  displayId: string;
  forcedMatch: boolean;
  openedAt: string;
  closedAt: string;
  paidAt: string;
  processingTime: string;
  net: number;
  gross: number;
  categories?: ITipCalculatorTicketCategory[];
  override?: ITipCalculatorTicketIgnoreOverride | ITipCalculatorTicketSplitOverride;
  totals: ITipCalculatorTicketTotals;
}
/* END -- Tip Calculator Request -- END */

/* BEGIN Tip Calculator Results -- BEGIN */
export interface ITipCalculatorResult {
  segmentId: string;
  segment: string;
  start: string;
  end: string;
  creditCardPercentFee?: number;
  employees: ITipCalculatorResultEmployee[];
  participants: ITipCalculatorResultParticipant[];
  rules: ICalculatorRule[];
  pools: ITipCalculatorResultPool[];
  totals: ITipCalculatorResultTotals;
  meta: ITipCalculatorResultMeta;
  warnings: ITipCalculatorResultWarning[];
}

export interface ITipCalculatorResultMeta {
  categoryGroups: ICategoryGroup[];
  participants: ICalculatorParticipant[];
}

export interface ITipCalculatorResultCalculatedTotals {
  total: number;
  autoGratuities: number;
  tips: number;
}

export interface ITipCalculatorResultCategory {
  name: string;
  price: number;
  quantity: number;
  discounts: number;
  tax: number;
  total: number;
  type: ShareRuleType;
}

export interface ITipCalculatorResultContribution {
  category?: string;
  eligibleAmount: number;
  eligibilityMethod: ShareEligibilityMethod;
  beneficiaries: { name: string; type: CalculatorParticipantType; split: CalculatorSplitMethod }[];
  share: number;
  sources: { source: RuleFundSource; total: number }[];
  total: number;
  type: ShareRuleType;
}

export interface ITipCalculatorResultReceived {
  category?: string;
  conditions?: IRemainderCondition[];
  distribution: TipCalculatorReceivedDistributionType;
  eligibilityMethod: ShareEligibilityMethod;
  from: ICalculatorParticipant;
  share: number;
  sources: { source: RuleFundSource; total: number }[];
  split: CalculatorSplitMethod;
  total: number;
  type: ShareRuleType;
}

export interface ITipCalculatorResultIgnoredCheck {
  displayId: string;
  ticketId: string;
  totals: ITipCalculatorBaseTotals;
}

export interface ITipCalculatorResultSplitCheckContribution {
  displayId: string;
  ticketId: string;
  totals: ITipCalculatorBaseTotals;
}

export interface ITipCalculatorResultSplitCheckReceived {
  displayId: string;
  ticketId: string;
  totals: ITipCalculatorResultCalculatedTotals;
  percent: number;
  sources: RuleFundSource[];
}

export interface ITipCalculatorResultSplitChecks {
  contributions: ITipCalculatorResultSplitCheckContribution[];
  received: ITipCalculatorResultSplitCheckReceived[];
}

export interface ITipCalculatorResultEmployee {
  id: string;
  name: string;
  calculated: ITipCalculatorResultCalculatedTotals;
  categories: ITipCalculatorResultCategory[];
  contributions?: ITipCalculatorResultContribution[];
  ignoredChecks?: ITipCalculatorResultIgnoredCheck[];
  job: IJob;
  received: ITipCalculatorResultReceived[];
  retained: ITipCalculatorResultCalculatedTotals;
  segmentId: string;
  splitChecks?: ITipCalculatorResultSplitChecks;
  totals: ITipCalculatorResultEmployeeTotals;
}

export interface ITipCalculatorResultParticipant {
  id: string;
  name: string;
  calculated: ITipCalculatorResultCalculatedTotals;
  categories: ITipCalculatorResultCategory[];
  contributions?: ITipCalculatorResultContribution[];
  received: ITipCalculatorResultReceived[];
  retained: ITipCalculatorResultCalculatedTotals;
  segmentId: string;
  splitChecks?: ITipCalculatorResultSplitChecks;
  totals: ITipCalculatorBaseTotals;
  type: CalculatorParticipantType;
}

export interface ITipCalculatorResultEmployeeTotals extends ITipCalculatorBaseTotals {
  totalMinWorked: number;
}

export interface ITipCalculatorResultPool {
  calculated: ITipCalculatorResultCalculatedTotals;
  categories: ITipCalculatorResultCategory[];
  contributions: ITipCalculatorResultContribution[];
  job: IJob;
  received: ITipCalculatorResultReceived[];
  retained: ITipCalculatorResultCalculatedTotals;
  totals: ITipCalculatorResultEmployeeTotals;
}

export interface ITipCalculatorResultTotals {
  allocated: ITipCalculatorResultCalculatedTotals;
  collected: ITipCalculatorResultCalculatedTotals;
}

export interface ITipCalculatorResultFunds {
  allocated: number;
  disbursed: number;
  diff: number;
}

interface ITipCalculatorResultWarningBase {
  type: TipCalculatorWarningType;
  segment?: string;
  contributor: ICalculatorParticipant;
  funds?: ITipCalculatorResultFunds;
}

export interface ITipCalculatorResultWarningCappedShare extends ITipCalculatorResultWarningBase {
  type: TipCalculatorWarningType.CAPPED_SHARE;
  employee: { id: string; name: string };
  details: ITipCalculatorResultWarningCappedShareDetail[];
}

export interface ITipCalculatorResultWarningCappedShareDetailRule {
  category?: string;
  eligibilityMethod: ShareEligibilityMethod;
  share: number;
  sources: RuleFundSource[];
  type: ShareRuleType;
  beneficiaries: IBeneficiary[];
}

export interface ITipCalculatorResultWarningCappedShareDetail {
  funds: ITipCalculatorResultFunds;
  rule: ITipCalculatorResultWarningCappedShareDetailRule;
}

export interface ITipCalculatorResultWarningCappedRemainder extends ITipCalculatorResultWarningBase {
  type: TipCalculatorWarningType.CAPPED_REMAINDER;
  details: ITipCalculatorResultWarningCappedRemainderDetail[];
}

export interface ITipCalculatorResultWarningCappedRemainderDetail {
  funds: ITipCalculatorResultFunds;
  rule: { beneficiaries: IRemainderBeneficiary[] };
}

export interface ITipCalculatorResultWarningUnallocatedRemainder extends ITipCalculatorResultWarningBase {
  type: TipCalculatorWarningType.UNALLOCATED_REMAINDER;
  contributor: ICalculatorParticipant;
  beneficiaries: {
    participant: { name: string; type: CalculatorParticipantType };
    received: ITipCalculatorResultReceived[];
  }[];
  remainder: {
    total: ITipCalculatorResultCalculatedTotals;
    unallocated: ITipCalculatorResultCalculatedTotals;
  };
  isSharedTerminal: boolean;
}

export type ITipCalculatorResultWarning = ITipCalculatorResultWarningCappedShare | ITipCalculatorResultWarningCappedRemainder | ITipCalculatorResultWarningUnallocatedRemainder;
/*  END -- Tip Calculator Results -- END */

/* BEGIN -- Tip Calculator Change Log --BEGIN */
export enum ChangeLogEntity {
  LABOR_DATA = 'labor_data',
  TIP_DATA = 'tip_data',
  CHECK = 'check',
}

export enum ChangeLogParticipantType {
  JOB = 'job',
  HOUSE_ACCOUNT = 'house_account',
  EMPLOYEE = 'employee',
}

export interface ChangeLogParticipant {
  name: string;
  type: ChangeLogParticipantType;
  job?: string;
}

export enum ChangeLogAction {
  SEGMENT_DURATION_EDITED = 'segment_duration_edited',
  SEGMENT_REMOVED = 'segment_removed',
  REMOVED = 'removed',
  CASH_TIPS_EDITED = 'cash_tips_edited',
  CREDIT_CARD_TIPS_EDITED = 'credit_card_tips_edited',
  CASH_AUTOGRATS_EDITED = 'cash_autograts_edited',
  CREDIT_AUTOGRATS_EDITED = 'credit_autograts_edited',
  IGNORE = 'ignore',
  SPLIT = 'split',
  UNDO_OVERRIDE = 'undo_override',
  CASH_EDITED = 'cash_edited',
}

export interface LaborDataDetails {
  id: string;
  from?: number;
  to?: number;
}

export interface TipEditedDetails {
  id: string;
  from: number;
  to: number;
}

export interface CheckModifiedDetails {
  id: string;
  displayId: string;
  from?: number;
  to?: number;
}

export type ChangeLogDetails = LaborDataDetails | TipEditedDetails | CheckModifiedDetails;

export interface ITipCalculatorChangeLogEntry {
  entity: ChangeLogEntity;
  action: ChangeLogAction;
  participant: ChangeLogParticipant;
  segment?: string;
  details?: ChangeLogDetails;
  timestamp: string;
}
/* END -- Tip Calculator Change Log --END */

export interface IUserPayoutCalculations {
  method: TipCalculatorMethod;
  payoutId: string;
  results: ITipCalculatorResult[];
}

export interface ICheck extends IPOSTicket {
  segmentName: string;
  employeeName: string;
  selected?: boolean;
}

export interface ITipCalculator {
  posResults?: IPOSResults;
  paymentSet?: ICalculatorPaymentSet;
  payees?: ITipCalculatorPayee[];
  results?: ITipCalculatorResult[];
  changeLog?: ITipCalculatorChangeLogEntry[];
}

export interface ITipCalculatorEmployeeTicket {
  ticketId: string;
  displayId?: string;
  forcedMatch: boolean;
  processingTime: string;
  creditCardFee: number;
  tip: number;
  netTip: number;
  totals: {
    gross: number;
    net: number;
    tip: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
    autoGratuity: { gross: number; net: number; cash: number; creditCard: number; creditCardFee: number };
  };
  categories: IPOSTicketCategory[];
  override?: IPOSTicketIgnoreOverride | IPOSTicketSplitOverride;
  employee?: {
    employeeId: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    customerEmployeeId: string;
  };
}

export interface IPayeeLaborData {
  segment?: ICalculatorPaymentSegment;
  job: IJob;
  totalMinWorked: number;
  error?: any;
}

export interface ICalculatorPaymentSegment extends IDaySegment {
  cash: number;
  credit: number;
  creditCardFee: number;
  hours?: string;
}

export interface ICalculatorPaymentSet {
  date: string;
  siteId: string;
  cash: number;
  credit: number;
  creditCardFee: number;
  segments: ICalculatorPaymentSegment[];
  employees: ITipCalculatorPayee[];
  description?: string;
}

export interface ICollectedTipsData {
  payee?: string;
  daySegment?: string;
  jobTitle?: string;
  cashTips?: number;
  cashAutograts?: number;
  ccTips?: number;
  ccAutograts?: number;
  ccFee?: number;
  net?: number;
}

export interface IReceivedTipsData {
  payee?: string;
  daySegment?: string;
  jobTitle?: string;
  duration?: number;
  receivedFrom?: string;
  receivedTips?: number;
  receivedAutograts?: number;
}

export interface IFormCalculatorParticipant extends ICalculatorParticipant {
  id: string;
}

export interface IFormBeneficiary extends IBeneficiary {
  id: string;
}

export interface IFormRemainderBeneficiary extends IRemainderBeneficiary {
  id: string;
  source?: string;
  condition?: IRemainderCondition;
  participants: IFormCalculatorParticipant[];
}

export interface IFormCalculatorRule extends ICalculatorRule {
  id: string;
  value: any;
  isBenefitingFromShare?: boolean;
  isBenefitingFromDefault?: boolean;
}

export interface IFormValues extends Omit<ICreateUpdateCalculatorRules, 'rules'> {
  rules: IFormCalculatorRule[];
}

export interface ITipShareFormValues extends Omit<ICalculatorRule, 'sharePolicy'> {
  id: string;
  sharePolicy: IFormCalculatorRuleSharePolicy;
}

export interface IFormCalculatorRuleSharePolicy extends Omit<ICalculatorRuleSharePolicy, 'shares'> {
  shares: IFormCalculatorRuleShare[];
  remainder: IFormCalculatorRuleRemainder;
}

export interface IFormCalculatorRuleShare extends Omit<ICalculatorRuleSharePolicyShare, 'beneficiaries'> {
  id: string;
  beneficiaries: IFormBeneficiary[];
}

export interface IFormCalculatorRuleRemainder extends ICalculatorRuleRemainder {
  beneficiaries: IFormRemainderBeneficiary[];
}

export interface IFormDefaultPool {
  includeNoLabor: boolean;
  contributingJobs: IFormCalculatorParticipant[];
  benefitingJobs: IFormCalculatorParticipant[];
}

export interface IRuleConflict extends ICalculatorRuleSharePolicyShare {
  tipShare: string;
}

export interface ITipCalculatorPayee {
  id: string;
  name: string;
  calculations: ITipCalculatorResultEmployee[];
  employee?: IPOSEmployee;
  provider?: IPOSProvider;
  segments?: IPOSSegment[];
  gross: number;
  net: number;
  autoGratuity: number;
  holdbackAmount: number;
  note?: string;
}

export interface ITipCalculatorParticipant {
  name: string;
  type: CalculatorParticipantType;
  calculations: ITipCalculatorResultParticipant[];
  segments?: IPOSSegment[];
  gross: number;
  net: number;
  autoGratuity: number;
}

export interface IPoolJob {
  jobTitle: string;
  totalMinutes: number;
  totalTips: number;
}

export interface IPoolData {
  daySegment: string;
  totalMinutes: number;
  totalTips: number;
  pools: IPoolJob[];
  result: ITipCalculatorResult;
}

export enum TipCalculatorEligibilityMethodLabel {
  by_segment = 'segment',
  by_ticket = 'check',
}

interface IExportRuleSettings extends ICalculatorRules {
  type: 'advanced' | 'standard';
}

export interface IExportCalculatorSettings {
  exportedAt: string;
  ruleSettings: IExportRuleSettings;
  segmentSettings: {
    segments: IDaySegment[];
  };
  site: {
    id: string;
    name: string;
  };
}
